import { useAuth0 } from "@auth0/auth0-react";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import type { AlertColor } from "@mui/material/Alert/Alert";
import type React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import useApi from "../../../api/apiConfig";
import type QueryClient from "../../../api/query/queryClient";
import useServerSentEvents from "../../../hooks/useServerSentEvents";
import { PREDEFINED_INSURANCE_VERIFICATION_FILTERS } from "../../../status";
import type { BackendFilters } from "../../../utils/backendFilters";
import useDevMode from "../../../utils/devmode";
import LiveChangesPill from "../../LiveSync/LiveChangesPill";
import { LiveRowMarksContext } from "../../LiveSync/LiveRowMarks";
import { liveRowClassName, liveRowSx } from "../../LiveSync/liveRowStyles";
import useLiveListSync from "../../LiveSync/useLiveListSync";
import { useInsuranceVerificationsPostingPoll } from "../../polling";
import {
  fetchInsuranceVerificationChanges,
  type InsuranceVerificationSearchParams,
} from "../api";
import {
  IV_LIST_KEY,
  ivListQueryKey,
  useInsuranceVerificationList,
} from "../hooks";
import { INSURANCE_VERIFICATION_PERSIST_KEY } from "../store";
import InsuranceVerificationTable from "../table";

interface VerificationListViewProps {
  searchParams: InsuranceVerificationSearchParams;
  queryClient: QueryClient;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  updateVerification: (payload: InsuranceVerificationUpdateMessage) => void;
  postToPMS: (verification: InsuranceVerificationMessage) => void;
  unpost: (verification: InsuranceVerificationMessage) => void;
  markManuallyPosted?: (verification: InsuranceVerificationMessage) => void;
  selectedFilter: PREDEFINED_INSURANCE_VERIFICATION_FILTERS;
  setSelectedFilter: (
    filter: PREDEFINED_INSURANCE_VERIFICATION_FILTERS,
  ) => void;
  selectedPractices: PracticeMessage[];
  includeAutoFiltered: boolean;
  setIncludeAutoFiltered: (include: boolean) => void;
  onFilterChange?: (filters: BackendFilters) => void;
  onSortChange?: (field: string, direction: "asc" | "desc") => void;
  page: number;
  pageSize: number;
  onPageChange?: (page: number, pageSize: number) => void;
}

const getVerificationId = (v: InsuranceVerificationMessage) => v.wieldyId;

export default function VerificationListView({
  searchParams: listParams,
  queryClient,
  updateSnackBar,
  updateVerification,
  postToPMS,
  unpost,
  markManuallyPosted,
  selectedFilter,
  setSelectedFilter,
  selectedPractices,
  includeAutoFiltered,
  setIncludeAutoFiltered,
  onFilterChange,
  onSortChange,
  page,
  pageSize,
  onPageChange,
}: VerificationListViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isDevMode = useDevMode();
  const { data, isLoading, isPlaceholderData, isError, errorUpdatedAt } =
    useInsuranceVerificationList(listParams);
  const verifications = useMemo(
    () => (data?.data ?? []) as InsuranceVerificationMessage[],
    [data],
  );

  const { getClient } = useApi();
  const { user } = useAuth0();
  const fetchChanges = useCallback(
    async (ids: string[]) =>
      fetchInsuranceVerificationChanges(await getClient(), ids, listParams),
    [getClient, listParams],
  );
  const liveSync = useLiveListSync({
    listKey: IV_LIST_KEY,
    queryKey: ivListQueryKey(listParams),
    getRowId: getVerificationId,
    fetchChanges,
    currentUserId: user?.sub,
  });
  useServerSentEvents({
    practiceIds: selectedPractices.map((p) => p.wieldyId),
    topics: ["verification"],
    onChange: liveSync.onChange,
  });

  useInsuranceVerificationsPostingPoll(
    verifications,
    queryClient,
    updateSnackBar,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: errorUpdatedAt marks each new failure; adding isError or updateSnackBar would re-show the snackbar
  useEffect(() => {
    if (!isError) return;
    updateSnackBar({
      severity: "error",
      message: "Failed to load insurance verifications",
    });
  }, [errorUpdatedAt]);

  const filterFromQueryParam =
    (): PREDEFINED_INSURANCE_VERIFICATION_FILTERS => {
      const statusParam = searchParams.get("status");
      if (!statusParam) {
        return PREDEFINED_INSURANCE_VERIFICATION_FILTERS.ALL;
      }
      const formattedStatus =
        statusParam.charAt(0).toUpperCase() +
        statusParam.slice(1).toLowerCase();
      return Object.values(PREDEFINED_INSURANCE_VERIFICATION_FILTERS).includes(
        formattedStatus as PREDEFINED_INSURANCE_VERIFICATION_FILTERS,
      )
        ? (formattedStatus as PREDEFINED_INSURANCE_VERIFICATION_FILTERS)
        : PREDEFINED_INSURANCE_VERIFICATION_FILTERS.ALL;
    };

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding filterFromQueryParam, setSelectedFilter would change when this runs
  useEffect(() => {
    setSelectedFilter(filterFromQueryParam());
  }, []);

  useEffect(() => {
    if (
      !isDevMode &&
      selectedFilter === PREDEFINED_INSURANCE_VERIFICATION_FILTERS.QUEUED
    ) {
      setSelectedFilter(PREDEFINED_INSURANCE_VERIFICATION_FILTERS.REVIEW);
    }
  }, [isDevMode, selectedFilter, setSelectedFilter]);

  const handleFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: PREDEFINED_INSURANCE_VERIFICATION_FILTERS | null,
  ) => {
    if (newFilter !== null && selectedFilter !== newFilter) {
      const params = new URLSearchParams(location.search);
      params.set("status", newFilter.toLowerCase());
      navigate({ search: params.toString() }, { replace: true });
      setSelectedFilter(newFilter);
    }
  };

  const selectedPracticeIds = new Set(selectedPractices.map((p) => p.wieldyId));

  const filteredVerifications = verifications.filter((v) =>
    selectedPracticeIds.has(v.practiceId),
  );

  return (
    <>
      <h1>Insurance Verifications</h1>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <ToggleButtonGroup
          value={selectedFilter}
          exclusive
          onChange={handleFilterChange}
          aria-label="insurance verification filter"
          sx={{ marginBottom: 2 }}
        >
          <ToggleButton
            value={PREDEFINED_INSURANCE_VERIFICATION_FILTERS.REVIEW}
          >
            Review
          </ToggleButton>
          {isDevMode && (
            <ToggleButton
              value={PREDEFINED_INSURANCE_VERIFICATION_FILTERS.QUEUED}
            >
              Queued
            </ToggleButton>
          )}
          <ToggleButton value={PREDEFINED_INSURANCE_VERIFICATION_FILTERS.ALL}>
            All
          </ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ marginBottom: 2 }}>
          <LiveChangesPill
            count={liveSync.pendingCount}
            onRefresh={liveSync.refresh}
          />
        </Box>
      </Box>
      {isDevMode &&
        selectedFilter === PREDEFINED_INSURANCE_VERIFICATION_FILTERS.ALL && (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={includeAutoFiltered}
                  onChange={() => setIncludeAutoFiltered(!includeAutoFiltered)}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Include Auto-Filtered Items
                  <Tooltip
                    title={`Show items that are ARCHIVED, MISSED, or NOT_APPLICABLE.
                    Useful for debugging system logic and verification rules.`}
                  >
                    <InfoIcon
                      sx={{
                        fontSize: 16,
                        color: "text.secondary",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                </Box>
              }
              labelPlacement="start"
            />
          </Box>
        )}
      <LiveRowMarksContext.Provider value={liveSync.marks}>
        <InsuranceVerificationTable
          isLoading={isLoading || isPlaceholderData}
          verifications={filteredVerifications}
          updateVerification={updateVerification}
          postToPMS={postToPMS}
          unpost={unpost}
          markManuallyPosted={markManuallyPosted}
          rowCount={data?.total}
          persistKey={INSURANCE_VERIFICATION_PERSIST_KEY}
          selectedFilter={selectedFilter}
          selectedPractices={selectedPractices}
          onFilterChange={onFilterChange}
          onSortChange={onSortChange}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          getRowClassName={(row) =>
            liveRowClassName(liveSync.marks.get(row.wieldyId))
          }
          sx={liveRowSx}
        />
      </LiveRowMarksContext.Provider>
    </>
  );
}
